/**
 * WebAuthn JSON glue between helm-api (webauthn-rs: binary fields as unpadded base64url strings)
 * and `navigator.credentials` (ArrayBuffers). Written by hand rather than relying on
 * `PublicKeyCredential.parseCreationOptionsFromJSON` / `toJSON()`, which older Safari lacks.
 * Nothing here logs or stores credential material.
 */

export function b64urlEncode(data: ArrayBuffer | ArrayBufferView): string {
  const bytes =
    data instanceof ArrayBuffer
      ? new Uint8Array(data)
      : new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function b64urlDecode(s: string): Uint8Array<ArrayBuffer> {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4));
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

type JsonDescriptor = { type: string; id: string; transports?: string[] };

export type CreationOptionsJSON = {
  publicKey: {
    rp: { id?: string; name: string };
    user: { id: string; name: string; displayName: string };
    challenge: string;
    pubKeyCredParams: Array<{ type: string; alg: number }>;
    timeout?: number;
    excludeCredentials?: JsonDescriptor[];
    authenticatorSelection?: AuthenticatorSelectionCriteria;
    attestation?: AttestationConveyancePreference;
    extensions?: Record<string, unknown>;
  };
};

export type RequestOptionsJSON = {
  publicKey: {
    challenge: string;
    timeout?: number;
    rpId?: string;
    allowCredentials?: JsonDescriptor[];
    userVerification?: string;
    extensions?: Record<string, unknown>;
  };
};

function descriptors(list: JsonDescriptor[] | undefined): PublicKeyCredentialDescriptor[] {
  return (list ?? []).map((d) => ({
    type: "public-key",
    id: b64urlDecode(d.id),
    ...(d.transports ? { transports: d.transports as AuthenticatorTransport[] } : {}),
  }));
}

/** Server registration options → `navigator.credentials.create({ publicKey })`. */
export function creationOptionsFromJSON(json: CreationOptionsJSON): PublicKeyCredentialCreationOptions {
  const pk = json.publicKey;
  return {
    rp: pk.rp,
    user: { id: b64urlDecode(pk.user.id), name: pk.user.name, displayName: pk.user.displayName },
    challenge: b64urlDecode(pk.challenge),
    pubKeyCredParams: pk.pubKeyCredParams.map((p) => ({ type: "public-key", alg: p.alg })),
    ...(pk.timeout !== undefined ? { timeout: pk.timeout } : {}),
    excludeCredentials: descriptors(pk.excludeCredentials),
    ...(pk.authenticatorSelection ? { authenticatorSelection: pk.authenticatorSelection } : {}),
    ...(pk.attestation ? { attestation: pk.attestation } : {}),
  };
}

/** Server sign-in options → `navigator.credentials.get({ publicKey })`. */
export function requestOptionsFromJSON(json: RequestOptionsJSON): PublicKeyCredentialRequestOptions {
  const pk = json.publicKey;
  return {
    challenge: b64urlDecode(pk.challenge),
    ...(pk.timeout !== undefined ? { timeout: pk.timeout } : {}),
    ...(pk.rpId ? { rpId: pk.rpId } : {}),
    allowCredentials: descriptors(pk.allowCredentials),
    ...(pk.userVerification ? { userVerification: pk.userVerification as UserVerificationRequirement } : {}),
  };
}

/** The subset of `PublicKeyCredential` the serialisers read (keeps them testable). */
export type CredentialLike = {
  id: string;
  rawId: ArrayBuffer;
  type: string;
  response: {
    clientDataJSON: ArrayBuffer;
    attestationObject?: ArrayBuffer;
    authenticatorData?: ArrayBuffer;
    signature?: ArrayBuffer;
    userHandle?: ArrayBuffer | null;
  };
};

/** `navigator.credentials.create()` result → the JSON webauthn-rs parses. */
export function registrationToJSON(cred: CredentialLike): Record<string, unknown> {
  const r = cred.response;
  if (!r.attestationObject) throw new Error("not a registration response");
  return {
    id: cred.id,
    rawId: b64urlEncode(cred.rawId),
    type: cred.type,
    response: {
      attestationObject: b64urlEncode(r.attestationObject),
      clientDataJSON: b64urlEncode(r.clientDataJSON),
      // Transports are omitted: webauthn-rs rejects values it does not know (e.g. "smart-card").
    },
    extensions: {},
  };
}

/** `navigator.credentials.get()` result → the JSON webauthn-rs parses. */
export function assertionToJSON(cred: CredentialLike): Record<string, unknown> {
  const r = cred.response;
  if (!r.authenticatorData || !r.signature) throw new Error("not an assertion response");
  return {
    id: cred.id,
    rawId: b64urlEncode(cred.rawId),
    type: cred.type,
    response: {
      authenticatorData: b64urlEncode(r.authenticatorData),
      clientDataJSON: b64urlEncode(r.clientDataJSON),
      signature: b64urlEncode(r.signature),
      userHandle: r.userHandle ? b64urlEncode(r.userHandle) : null,
    },
    extensions: {},
  };
}

export function webauthnSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.PublicKeyCredential === "function" &&
    // `navigator.credentials` is missing outside secure contexts.
    "credentials" in navigator
  );
}

/** A user-facing message for a failed `navigator.credentials` call. */
export function webauthnErrorMessage(e: unknown): string {
  const name = e instanceof Error ? e.name : "";
  if (name === "NotAllowedError") return "The passkey prompt was cancelled or timed out.";
  if (name === "InvalidStateError") return "This authenticator already holds a TradePrizm passkey.";
  if (name === "SecurityError")
    return "Passkeys are not available on this address (check HELM_PUBLIC_ORIGIN).";
  if (name === "NotSupportedError") return "This device does not support passkeys.";
  return e instanceof Error ? e.message : "Passkey operation failed.";
}
